<template>

  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="$emit('close')">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                         leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-95 transition-opacity"/>
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300"
                         enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                         enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                         leave-from="opacity-100 translate-y-0 sm:scale-100"
                         leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
              class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
            <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
              <button
                  type="button"
                  class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  @click="$emit('close')"
              >
                <span class="sr-only">Close</span>
                <XIcon class="h-6 w-6" aria-hidden="true"/>
              </button>
            </div>

            <div class="mt-3 text-center sm:mt-0  sm:text-left">
              <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
                Copy Project
              </DialogTitle>
              <div class="mt-8">
<!--                <p class="text-sm text-gray-500">-->
<!--                  Share this workspace with team members to view or edit.-->
<!--                </p>-->

                <div>
                  <label for="name`" class="block text-gray-800 pb-2">
                    New project name
                  </label>
                  <div class="mt-1" :class="[{'relative rounded-md shadow-sm': !isValidName}]">
                    <input
                        v-model="name"
                        @input="debounceCheckName()"
                        type="text"
                        name="name"
                        :id="name"
                        class="shadow-sm focus:ring-primary focus:border-primary block w-full border-gray-300 rounded-md text-sm tracking-wide"
                        :class="[{'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500 pr-10': !isValidName}]"
                        :placeholder="'Enter project name'"
                        aria-invalid="true"
                        aria-describedby="name-error"
                    />
                    <div v-show="!isValidName"
                         class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon class="h-5 w-5 text-red-500" aria-hidden="true"/>
                    </div>
                  </div>
                  <p v-show="!isValidName" class="mt-2 text-sm text-red-600" id="name-error">
                    Keep empty or put a valid project name.
                  </p>
                </div>





                <div class="mt-8">
                  <div class="block text-gray-800 pb-4">What would you like to copy? </div>

                  <div class="flex flex-wrap space-y-2">

                    <div v-for="(copyOptionValue, copyOptionName, idx) in copyOptions"
                         :key="'copy-option-`${idx}`'"
                         class="mt-1 w-1/2 sm:w-1/3"
                    >
                      <label :for="`copy-option-${idx}`"
                             class="block font-base text-sm pb-2 flex space-x-2 "
                             :class="{'cursor-pointer text-gray-700': copyOptionValue.enabled, 'cursor-not-allowed text-gray-400': !copyOptionValue.enabled}"
                      >
                        <input
                            :checked="copyOptions[copyOptionName].value"
                            @input="setOptionValue(copyOptionName, !copyOptions[copyOptionName].value)"
                            :id="`copy-option-${idx}`"
                            type="checkbox"
                            class="focus:ring-0 h-5 w-5 border-gray-300 rounded "
                            :class="{'cursor-pointer text-primary-600': copyOptionValue.enabled, 'cursor-not-allowed text-gray-400': !copyOptionValue.enabled}"
                            :disabled="!copyOptionValue.enabled"
                        />
                        <span>{{ copyOptionValue.label }}</span>
                      </label>
                    </div>

                  </div>

                </div>




              </div>
            </div>


            <div class="mt-16 sm:flex sm:flex-row-reverse">
              <button
                  @click="copyWorkspace()"
                  :class="{'cursor-not-allowed ': hasAtLeastOneError, 'hover:bg-ternary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ternary-500': !hasAtLeastOneError}"
                  type="button"
                  class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-ternary-500 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm"
                  :disabled="hasAtLeastOneError"
              >
                Copy Project
              </button>
              <button
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ternary-500 sm:mt-0 sm:w-auto sm:text-sm"
                  @click="$emit('close')"
              >
                Cancel
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

</template>

<script>
import _debounce from 'lodash.debounce'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {CheckIcon, ExclamationIcon, PlusCircleIcon, PlusIcon, XCircleIcon, XIcon} from '@heroicons/vue/outline'
import {ExclamationCircleIcon} from '@heroicons/vue/solid'
import {mapGetters} from "vuex";


export default {
  name: "WorkspaceCopyForm",
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    ExclamationIcon,
    XIcon,
    ExclamationCircleIcon,
    PlusIcon,
    PlusCircleIcon,
    CheckIcon,
    XCircleIcon
  },
  props: {
    open: Boolean,
  },
  data() {
    return {
      name: `${this.getWorkspace().name} (copy)`,
      isValidName: true,
      copyOptions: {
        innovation: {value: true, label: 'Innovation project', enabled: true},
        channels: {value: true, label: 'Lists', enabled: true},
        channelElements: {value: true, label: 'Lists content', enabled: true},
        attachments: {value: true, label: 'My attachments', enabled: true},
        userPreferences: {value: true, label: 'My settings', enabled: true},
        groups: {value: true, label: 'Groups', enabled: true},
      },

      messages: [
        {key: "201", value: "Invitation sent"},
      ],

    }
  },
  methods: {
    debounceCheckName: _debounce(function () {
      this.checkName();
    }, 300),

    checkName() {
      let regexName = /^$|^[^!"$^`]{1,128}$/;
      this.isValidName = regexName.test(this.name);
    },

    setOptionValue(name, value)
    {
      this.copyOptions[name].value = value;

      if(name === 'channels') {
        this.copyOptions.channelElements.enabled = (this.copyOptions.channels.value === true)
      }
    },

    copyWorkspace() {
          this.checkName();
          if (!this.isValidName) {
            return;
          }

          this.$store.dispatch("copyWorkspace", {
            workspace: this.getWorkspace(),
            name: this.name,
            userPreferences: this.copyOptions.userPreferences.value,
            innovation: this.copyOptions.innovation.value,
            groups: this.copyOptions.groups.value,
            channels: this.copyOptions.channels.value,
            channelElements: this.copyOptions.channels.value ? this.copyOptions.channelElements.value : false,
            attachments: this.copyOptions.attachments.value,
          });


        this.$store.commit('addNotification', {
          type: 'info',
          title: `Processing the project copy`,
          body: 'The copy of your project is running in the background. This may take a few moments.',
        });

        this.$emit('close');
    },

    getMessage(key) {
      key = '' + key;
      return this.messages.find(item => {
        return item.key === key;
      })["value"];
    },

    ...mapGetters({
      getWorkspace: 'getWorkspaceForCopyWorkspacePopup',
    }),
  },

  computed: {
    hasAtLeastOneError() {
      return !this.isValidName;
    },
  }
}
</script>
